import { X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BookingSlot } from '@/types/scheduling.types';

interface ConfirmationModalProps {
  isOpen: boolean;
  slot: BookingSlot | null;
  timezone: string;
  isRescheduling?: boolean;
  onClose: () => void;
  onConfirm: (notes?: string) => Promise<void>;
}

function formatDate(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  });
}

function formatTime(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
}

function computeMinutes(startUtc: string, endUtc: string): number {
  return Math.round((new Date(endUtc).getTime() - new Date(startUtc).getTime()) / 60000);
}

export function ConfirmationModal({
  isOpen,
  slot,
  timezone,
  isRescheduling,
  onClose,
  onConfirm,
}: ConfirmationModalProps) {
  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);
  const [notes, setNotes] = useState('');

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return undefined;
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (!isOpen) {
      setNotes('');
    }
  }, [isOpen]);

  if (!isOpen || !slot) {
    return null;
  }

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm(notes || undefined);
    } finally {
      setIsConfirming(false);
    }
  };

  const date = formatDate(slot.startAtUtc, timezone);
  const startTime = formatTime(slot.startAtUtc, timezone);
  const endTime = formatTime(slot.endAtUtc, timezone);
  const duration = computeMinutes(slot.startAtUtc, slot.endAtUtc);

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='mx-4 w-full max-w-md rounded-lg bg-white shadow-xl'>
        <div className='flex items-center justify-between border-b px-6 py-4'>
          <h2 className='text-lg font-semibold text-gray-900'>
            {t(isRescheduling ? 'booking.reschedule.confirmButton' : 'booking.confirm.title')}
          </h2>
          <button
            type='button'
            onClick={onClose}
            className='rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'>
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='space-y-4 px-6 py-5'>
          <p className='text-sm text-gray-600'>{t('booking.confirm.message')}</p>

          <div className='space-y-3 rounded-lg bg-gray-50 p-4'>
            <div className='flex justify-between text-sm'>
              <span className='text-gray-500'>{t('booking.confirm.date')}</span>
              <span className='font-medium text-gray-900'>{date}</span>
            </div>
            <div className='flex justify-between text-sm'>
              <span className='text-gray-500'>{t('booking.confirm.time')}</span>
              <span className='font-medium text-gray-900'>
                {startTime} – {endTime}
              </span>
            </div>
            <div className='flex justify-between text-sm'>
              <span className='text-gray-500'>{t('booking.confirm.duration')}</span>
              <span className='font-medium text-gray-900'>{t('booking.confirm.minutes', { count: duration })}</span>
            </div>
          </div>

          {isRescheduling && (
            <div>
              <label htmlFor='reschedule-notes' className='block text-sm font-medium text-gray-700'>
                {t('booking.reschedule.notes')}
              </label>
              <textarea
                id='reschedule-notes'
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('booking.reschedule.notesPlaceholder')}
                rows={2}
                className='mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              />
            </div>
          )}
        </div>

        <div className='flex justify-end gap-3 border-t px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            disabled={isConfirming}
            className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'>
            {t('booking.confirm.cancel')}
          </button>
          <button
            type='button'
            onClick={() => void handleConfirm()}
            disabled={isConfirming}
            className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'>
            {isConfirming
              ? t(isRescheduling ? 'booking.reschedule.confirming' : 'booking.confirm.confirming')
              : t(isRescheduling ? 'booking.reschedule.confirmButton' : 'booking.confirm.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
